import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Where the pointer was, in viewport coordinates. */
export interface MenuAnchor {
  x: number;
  y: number;
}

/**
 * One entry. `action` is whatever the caller's own union is; the shell only
 * ever hands the whole row back, so a row may carry anything else it needs
 * (which session this acts on, for instance) without the shell knowing.
 */
export interface MenuRow {
  /** Distinct per row. Defaults to `action`, which is enough when actions are unique. */
  key?: string;
  action: string;
  label: string;
  tone?: 'danger';
  disabled?: boolean;
  /** Why this entry cannot be used right now. Never disable without one. */
  reason?: string;
}

interface Props<R extends MenuRow> {
  /** What this menu is about — a table number, a resource name. */
  title: string;
  /** A live detail under the title: the state, the meter, the occupancy. */
  subtitle?: string | null;
  anchor: MenuAnchor;
  items: R[];
  footnote?: string | null;
  onPick: (item: R) => void;
  onClose: () => void;
}

const MENU_WIDTH = 232;

/**
 * The floor's action menu — the shell that every entity on the map shares.
 *
 * §5.2 — a manager watching the floor should not have to find a thing on the
 * map, then hunt for a panel elsewhere to act on it. A single tap puts the work
 * under the pointer: what this thing can do right now, narrowed by what this
 * operator may do, in the order the shift actually runs.
 *
 * Tap, not right-click — this runs on tills and tablets, where there is no
 * second button and a long-press belongs to the browser.
 *
 * What lives here is only the behaviour that was tuned for those tills and must
 * not be forked per entity: staying on screen near an edge, dismissal on
 * `pointerdown`/Escape/scroll/resize, first-focus, and finger-sized rows that
 * always say why they are disabled. *Which* rows exist is the caller's
 * question, because only the caller knows the state machine and the permission
 * keys the server would enforce.
 */
export function ContextMenu<R extends MenuRow>({
  title,
  subtitle,
  anchor,
  items,
  footnote,
  onPick,
  onClose,
}: Props<R>) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(anchor);

  // Keep the menu on screen: a table near the right or bottom edge would
  // otherwise open a menu that runs off the viewport.
  useLayoutEffect(() => {
    const height = ref.current?.offsetHeight ?? 0;
    const width = ref.current?.offsetWidth ?? MENU_WIDTH;
    setPosition({
      x: Math.min(anchor.x, window.innerWidth - width - 8),
      y: Math.min(anchor.y, window.innerHeight - height - 8),
    });
  }, [anchor, items.length]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    const onAway = (event: Event) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    // `pointerdown`, not `mousedown`: this runs on tills and tablets, and a tap
    // outside has to dismiss. `capture` so it wins over a handler that stops
    // bubbling on the way up.
    document.addEventListener('pointerdown', onAway, true);
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onAway, true);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus();
  }, []);

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={title}
      style={{ top: position.y, left: position.x, minWidth: MENU_WIDTH }}
      className="fixed z-50 overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--popover,var(--card))] py-1 shadow-lg"
    >
      <div className="px-3 py-2 text-xs font-medium text-ink-muted">
        {title}
        {subtitle && (
          <span className="mt-0.5 block font-normal tabular-nums">{subtitle}</span>
        )}
      </div>
      {items.length === 0 ? (
        <div className="px-3 py-2 text-sm text-ink-muted">
          {t('spaces.menu.noActions')}
        </div>
      ) : (
        items.map((item) => (
          <button
            key={item.key ?? item.action}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            title={item.reason}
            onClick={() => onPick(item)}
            className={`block w-full px-4 py-3 text-start text-sm hover:bg-[var(--accent,var(--muted))] focus:bg-[var(--accent,var(--muted))] focus:outline-none disabled:cursor-not-allowed disabled:hover:bg-transparent ${
              item.tone === 'danger' ? 'text-[var(--destructive)]' : ''
            }`}
          >
            <span className={item.disabled ? 'text-ink-muted' : undefined}>
              {item.label}
            </span>
            {/* A control that is unavailable says why: a disabled row with no
                explanation is its own bug. */}
            {item.reason && (
              <span className="mt-0.5 block text-xs font-normal text-ink-muted">
                {item.reason}
              </span>
            )}
          </button>
        ))
      )}
      {footnote && (
        <p className="border-t border-[var(--border)] px-3 py-2 text-xs text-ink-muted">
          {footnote}
        </p>
      )}
    </div>
  );
}
